/**
 * useStates — load the US states once per session (`GET /v2/states/`).
 * Same shape as `useMaturityCredits`: a module-level singleton cache + a
 * mirrored state machine. Feeds the Facility Schedule "State" picker and
 * the Loss Runs "Venue" picker.
 */
import { useEffect, useState } from "react";
import { getStates } from "../api";
import type { UsState } from "../api/endpoints/states";

export type StatesQueryState =
    | { status: "loading" }
    | { status: "error"; error: Error }
    | { status: "ready"; data: UsState[] };

let inflight: Promise<UsState[]> | null = null;

function loadOnce(): Promise<UsState[]> {
    if (inflight) return inflight;
    inflight = getStates().catch((error) => {
        inflight = null;
        throw error;
    });
    return inflight;
}

export function useStates(): StatesQueryState {
    const [state, setState] = useState<StatesQueryState>({ status: "loading" });

    useEffect(() => {
        let cancelled = false;
        loadOnce()
            .then((data) => {
                if (cancelled) return;
                setState({ status: "ready", data });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setState({
                    status: "error",
                    error: error instanceof Error ? error : new Error("Failed to load states"),
                });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return state;
}
